import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Plus, MapPin, Calendar, Ticket, ArrowRight, AlertTriangle } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
      const statsMap = {};
      await Promise.all(
        data.map(async (ev) => {
          const r = await api.get(`/events/${ev.id}/stats`);
          statsMap[ev.id] = r.data;
        }),
      );
      setStats(statsMap);
    } catch (e) {
      setErr("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalEvents = events.length;
  const totalTickets = Object.values(stats).reduce((a, s) => a + (s?.total || 0), 0);
  const totalAttended = Object.values(stats).reduce((a, s) => a + (s?.attended || 0), 0);
  const totalRemaining = totalTickets - totalAttended;

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-dashboard">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="mono-label text-[#666] mb-2">// CONTROL ROOM</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
              Events
            </h1>
          </div>
          <Link
            to="/admin/events/new"
            className="brut-btn-accent inline-flex items-center gap-2 self-start sm:self-auto"
            data-testid="create-event-btn"
          >
            <Plus className="w-5 h-5" strokeWidth={3} />
            New Event
          </Link>
        </div>

        {/* Global stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="Events" value={totalEvents} testid="stat-events" />
          <StatCard label="Tickets Issued" value={totalTickets} testid="stat-issued" />
          <StatCard
            label="Attended"
            value={totalAttended}
            accent="#00C853"
            testid="stat-attended"
          />
          <StatCard label="Remaining" value={totalRemaining} testid="stat-remaining" />
        </div>

        {err && (
          <div
            className="brut-card-flat border-[#FF3B30] p-4 mb-6 flex items-center gap-2"
            data-testid="dashboard-error"
          >
            <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
            <span>{err}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20" data-testid="events-loading">
            <div className="brut-spinner" />
          </div>
        ) : events.length === 0 ? (
          <div className="brut-card p-12 text-center" data-testid="events-empty">
            <div className="mono-label text-[#666] mb-3">// NO EVENTS YET</div>
            <h3 className="font-display text-2xl font-bold uppercase mb-3">
              Start by creating your first event
            </h3>
            <p className="text-[#666] mb-6">
              Tickets are auto-generated when you create an event. You can then
              print them or distribute their QR codes off-platform.
            </p>
            <Link to="/admin/events/new" className="brut-btn-accent inline-flex items-center gap-2">
              <Plus className="w-5 h-5" strokeWidth={3} />
              Create Event
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {events.map((ev) => {
              const s = stats[ev.id] || { total: 0, attended: 0, remaining: 0, attendance_rate: 0 };
              const pct = s.attendance_rate;
              return (
                <Link
                  key={ev.id}
                  to={`/admin/events/${ev.id}`}
                  className="brut-card hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_#121212] transition-transform p-6 group"
                  data-testid={`event-card-${ev.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="mono-label text-[#666]">// {formatDate(ev.start_at).split(",")[0]}</div>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-3 line-clamp-2">
                    {ev.name}
                  </h3>
                  <div className="space-y-2 text-sm mb-5">
                    <div className="flex items-center gap-2 text-[#666]">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{ev.venue}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#666]">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(ev.start_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#666]">
                      <Ticket className="w-4 h-4" />
                      <span>{ev.ticket_count} tickets</span>
                    </div>
                  </div>

                  {/* Mini progress */}
                  <div className="border-t-2 border-[#121212] pt-3">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="mono-label">Attended</span>
                      <span className="font-mono text-sm font-bold">
                        {s.attended} / {s.total}
                      </span>
                    </div>
                    <div className="h-3 border-2 border-[#121212] bg-white relative overflow-hidden">
                      <div
                        className="h-full bg-[#00C853]"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="brut-card p-5" data-testid={testid}>
      <div className="mono-label text-[#666] mb-2">{label}</div>
      <div
        className="font-display text-4xl lg:text-5xl font-black tabular-nums"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
